import { unzip } from "fflate";

const isZipFile = (file) =>
  file?.type === "application/zip" ||
  file?.type === "application/x-zip-compressed" ||
  /\.zip$/i.test(file?.name || "");

const readAsUint8Array = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

// Skip directory entries and OS metadata that shouldn't be uploaded. Backend
// rejects these with a 500, so they must never be sent.
const shouldSkipEntry = (entryPath) => {
  const p = (entryPath || "").replace(/^\.?\//, "");
  if (!p || p.endsWith("/")) return true; // empty / directory placeholder
  if (p.startsWith("__MACOSX/") || p.includes("/__MACOSX/")) return true; // macOS resource forks
  const base = p.split("/").pop();
  return base === ".DS_Store" || base === "Thumbs.db";
};

// Resolve a file's in-folder path across the various sources (zip entry,
// react-dropzone `path`, native directory input `webkitRelativePath`).
const entryPathOf = (file) =>
  file?.relativePath ||
  file?.path ||
  file?.webkitRelativePath ||
  file?.name ||
  "";

/**
 * Drops OS-metadata / directory-placeholder files from a selection. Zip
 * expansion already filters these, but whole-folder uploads (which send files
 * straight from the OS) need the same treatment or the junk files 500.
 */
export const filterJunkFiles = (files) =>
  (files || []).filter((file) => !shouldSkipEntry(entryPathOf(file)));

const relPathOf = (file) =>
  (
    file?.relativePath ||
    file?.path ||
    file?.webkitRelativePath ||
    ""
  ).replace(/^\.?\//, "");

/**
 * Strips the single top-level wrapper folder from a selection so a folder's (or
 * a wrapped zip's) CONTENTS upload directly into the current location instead of
 * being nested under the picked folder's name — e.g. selecting "tekyantra/"
 * uploads "plugins/…", "themes/…", "wp-config.php" rather than
 * "tekyantra/plugins/…". Only strips when EVERY file shares the same first path
 * segment (one real wrapper folder); loose files or multi-root selections are
 * returned unchanged. Mutates each file's `relativePath` (read first by the
 * upload/tree code) so both the Review tree and the upload use the stripped path.
 */
export const stripCommonWrapper = (files) => {
  const list = files || [];
  if (list.length === 0) return list;
  const first = relPathOf(list[0]);
  if (!first.includes("/")) return list; // no folder structure → nothing to strip
  const wrapper = first.split("/")[0];
  const allShareWrapper = list.every((f) => {
    const rel = relPathOf(f);
    return rel === wrapper || rel.startsWith(wrapper + "/");
  });
  if (!allShareWrapper) return list;
  list.forEach((f) => {
    f.relativePath = relPathOf(f).slice(wrapper.length + 1);
  });
  return list;
};

const extractZip = (file) =>
  readAsUint8Array(file).then(
    (data) =>
      new Promise((resolve, reject) => {
        unzip(data, (err, unzipped) => {
          if (err) return reject(err);
          const extracted = Object.entries(unzipped)
            .filter(([entryPath]) => !shouldSkipEntry(entryPath))
            .map(([entryPath, content]) => {
              const name = entryPath.split("/").pop();
              const extractedFile = new File([content], name);
              // uploadFile() reads `path` to rebuild the folder structure.
              extractedFile.path = entryPath;
              return extractedFile;
            });
          resolve(extracted);
        });
      }),
  );

// A file is "nested inside a selected folder" when its relative path has a
// directory component (folder inputs set webkitRelativePath, dropzone sets
// path). Such a file belongs to the folder the user picked and must be uploaded
// as-is — even if it's a .zip (e.g. a WordPress uploads/*.zip).
const isNestedInFolder = (file) => {
  const rel = (
    file?.relativePath ||
    file?.path ||
    file?.webkitRelativePath ||
    ""
  ).replace(/^\.?\//, "");
  return rel.includes("/");
};

/**
 * Expands a .zip that the user picked DIRECTLY (a loose archive) into its
 * contained files so the folder structure is preserved. A .zip that lives
 * *inside* a selected folder is a real site file, not an archive to unpack, so
 * it's uploaded untouched (this is why a whole-folder upload no longer sprouts
 * extra folders from inner uploads/*.zip files). Non-zip files pass through. If
 * a directly-picked zip fails to extract, it falls back to uploading as-is.
 */
export const expandZipFiles = async (files) => {
  const result = [];
  for (const file of files) {
    if (isZipFile(file) && !isNestedInFolder(file)) {
      try {
        const extracted = await extractZip(file);
        result.push(...extracted);
      } catch (e) {
        console.error(`Failed to extract zip "${file?.name}":`, e);
        result.push(file);
      }
    } else {
      result.push(file);
    }
  }
  return result;
};
